import { useState, useRef, useEffect, useCallback, type ReactNode } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import { Pause, StickyNote, MessageCircle } from 'lucide-react';
import { SemmyAvatar } from '@/components/SemmyAvatar';
import { useSemmy } from '@/hooks/useSemmy';
import { BarInner } from '@/components/CommandBar';

// ── Types ─────────────────────────────────────────────────────────────────────

interface Message {
  role: 'operator' | 'semmy';
  content: string;
}

// ── Quick prompts ─────────────────────────────────────────────────────────────

const QUICK_PROMPTS = [
  { label: "Vat mijn leads samen",    query: "Geef me een samenvatting van mijn huidige leads en hun status." },
  { label: "Wat staat er open?",      query: "Welke acties van Semmy wachten op mijn goedkeuring?" },
  { label: "Hoe gaat het met klanten?", query: "Zijn er klanten waar ik extra aandacht aan moet besteden?" },
  { label: "Recente correcties",      query: "Wat heb ik de afgelopen week aangepast aan Semmy's berichten?" },
];

// ── Avatar ────────────────────────────────────────────────────────────────────

function OperatorAvatar({ initial }: { initial: string }) {
  return (
    <div
      className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-[11px] font-bold select-none"
      style={{ background: 'var(--bg-surface-raised)', color: 'var(--text-secondary)', border: '1px solid var(--border-primary)' }}
    >
      {initial}
    </div>
  );
}

// ── useStreamingText hook ─────────────────────────────────────────────────────

function useStreamingText(content: string, isStreaming: boolean) {
  const isEmpty = content.length === 0;
  return { displayText: content, showCursor: isStreaming && !isEmpty };
}

// ── Streaming paragraph reveal ────────────────────────────────────────────────

function splitAtLastBoundary(text: string): { committed: string; live: string } {
  const lastBoundary = text.lastIndexOf('\n\n');
  if (lastBoundary === -1) return { committed: '', live: text };
  return {
    committed: text.slice(0, lastBoundary).trim(),
    live:      text.slice(lastBoundary + 2),
  };
}

function StreamingContent({ content, isStreaming }: { content: string; isStreaming: boolean }) {
  if (!isStreaming) {
    return <span style={{ whiteSpace: 'pre-wrap' }}>{content}</span>;
  }

  const { committed, live } = splitAtLastBoundary(content);
  const paragraphs = committed ? committed.split('\n\n').filter(Boolean) : [];

  return (
    <>
      {paragraphs.map((para, i) => (
        <span
          key={i}
          className="streaming-paragraph"
          style={{ display: 'block', marginBottom: '0.4em' }}
        >
          {para}
        </span>
      ))}
      {live && (
        <span style={{ display: 'block' }}>
          {live}
          <span
            style={{
              display:       'inline-block',
              width:         '2px',
              height:        '13px',
              background:    'var(--accent)',
              marginLeft:    '2px',
              verticalAlign: 'middle',
              animation:     'pulse 1s ease-in-out infinite',
            }}
          />
        </span>
      )}
    </>
  );
}

// ── Intent chip (shown while streaming) ──────────────────────────────────────

function IntentChip({ active }: { active: boolean }) {
  if (!active) return null;
  return (
    <span className="inline-flex items-center gap-1.5 text-[10px] font-medium px-2 py-0.5 rounded-full border mb-1.5"
      style={{ background: 'var(--bg-input)', color: 'var(--text-muted)', borderColor: 'var(--border-separator)' }}>
      <span className="w-1.5 h-1.5 rounded-full animate-pulse-soft" style={{ background: 'var(--accent)' }} />
      Aan het schrijven...
    </span>
  );
}

// ── Thinking indicator ────────────────────────────────────────────────────────

function ThinkingDots() {
  const { resolved } = useTheme();
  const isLight = resolved === 'light';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 2px' }}>
      {[0, 1, 2].map(i => (
        <span
          key={i}
          style={{
            display: 'block',
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: isLight ? 'rgba(0,0,0,0.28)' : 'rgba(255,255,255,0.35)',
            animation: `semmy-dot-bounce 1.2s ease-in-out infinite`,
            animationDelay: `${i * 0.18}s`,
          }}
        />
      ))}
    </div>
  );
}

// ── Message bubble ────────────────────────────────────────────────────────────

function MessageBubble({ message, operatorInitial, isNew, isStreaming, isLatest, semmyImageUrl }: {
  message: Message;
  operatorInitial: string;
  isNew?: boolean;
  isStreaming?: boolean;
  isLatest?: boolean;
  semmyImageUrl?: string | null;
}) {
  const { resolved } = useTheme();
  const isLight = resolved === 'light';
  const isOperator = message.role === 'operator';
  const showIntentChip = !isOperator && (isStreaming ?? false) && message.content.length === 0;

  const surface = isOperator
    ? (isLight ? 'rgba(0,0,0,0.025)' : 'rgba(255,255,255,0.025)')
    : isLatest
      ? (isLight ? 'rgba(255,255,255,0.85)' : 'rgba(8,8,10,0.58)')
      : (isLight ? 'rgba(255,255,255,0.55)' : 'rgba(8,8,10,0.42)');

  const textColor = isOperator
    ? (isLight ? 'rgba(0,0,0,0.55)' : 'rgba(255,255,255,0.38)')
    : isLatest
      ? (isLight ? 'rgba(0,0,0,0.85)' : 'rgba(255,255,255,0.82)')
      : (isLight ? 'rgba(0,0,0,0.62)' : 'rgba(255,255,255,0.55)');

  const borderColor = isOperator
    ? (isLight ? '1px solid rgba(0,0,0,0.06)' : '1px solid rgba(255,255,255,0.05)')
    : isLatest
      ? '1px solid rgba(109,183,132,0.18)'
      : (isLight ? '1px solid rgba(0,0,0,0.05)' : '1px solid rgba(255,255,255,0.04)');

  const [showContent, setShowContent] = useState(false);
  useEffect(() => {
    if (isNew) {
      const t = setTimeout(() => setShowContent(true), 150);
      return () => clearTimeout(t);
    } else {
      setShowContent(true);
    }
  }, [isNew]);

  return (
    <div className={`flex gap-3 group ${isOperator ? 'flex-row-reverse' : ''} ${isNew ? 'msg-fade-up' : ''}`}>
      {isOperator
        ? <OperatorAvatar initial={operatorInitial} />
        : <SemmyAvatar size={36} imageUrl={semmyImageUrl} thinking={showIntentChip} />}
      <div className="flex flex-col max-w-[80%]">
        <div
          className="text-[11px] font-semibold tracking-wide px-1 select-none"
          style={{
            color: 'var(--text-tertiary)',
            textAlign: isOperator ? 'right' : 'left',
            marginBottom: 4,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            opacity: 0.6,
          }}
        >
          {isOperator ? 'You' : 'Semmy'}
        </div>
        {!isOperator && <IntentChip active={showIntentChip} />}
        <div
          className="px-4 py-3 text-[13px] leading-relaxed break-words"
          style={{
            position: 'relative',
            overflow: 'hidden',
            borderRadius: isOperator ? '16px 4px 16px 16px' : '4px 16px 16px 16px',
            background: surface,
            color: textColor,
            border: borderColor,
          }}
        >
          {!isOperator && (
            <span style={{
              position: 'absolute',
              ...(isLatest ? { top: 0 } : { bottom: 0 }),
              left: 0,
              right: 0,
              height: 1,
              background: isLatest
                ? 'linear-gradient(90deg, transparent, rgba(109,183,132,0) 10%, rgba(109,183,132,0.60) 50%, rgba(109,183,132,0) 90%, transparent)'
                : 'linear-gradient(90deg, transparent, rgba(109,183,132,0) 20%, rgba(109,183,132,0.18) 50%, rgba(109,183,132,0) 80%, transparent)',
              backgroundSize: '200% auto',
              animation: isLatest ? 'shimmer-latest 6s ease-in-out infinite' : 'shimmer-old 9s ease-in-out infinite',
              opacity: isLatest ? 1 : 0.5,
            }} />
          )}
          {showContent
            ? (message.content
                ? <StreamingContent content={message.content} isStreaming={isStreaming ?? false} />
                : (!isOperator && !isStreaming ? <ThinkingDots /> : null))
            : (!isOperator ? <ThinkingDots /> : null)
          }
        </div>
      </div>
    </div>
  );
}

interface QuickActionsProps {
  partnerId: string;
  onActionComplete: (message: string) => void;
}

type ActiveAction = 'pause_client' | 'add_note' | 'temp_instruction' | null;

function QuickActions({ partnerId, onActionComplete }: QuickActionsProps) {
  const [active, setActive] = useState<ActiveAction>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<{ id: string; name: string; phone: string }[]>([]);
  const [selectedClient, setSelectedClient] = useState<{ id: string; name: string } | null>(null);
  const [noteText, setNoteText] = useState('');
  const [instructionText, setInstructionText] = useState('');
  const [saving, setSaving] = useState(false);
  const [holdDays, setHoldDays] = useState(7);

  useEffect(() => {
    if (!partnerId || !searchQuery.trim() || searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }

    const timer = setTimeout(async () => {
const { data } = await (supabase
      .from('semmy_contacts')
      .select('id, confirmed_name, whatsapp_name, phone_number')
      .eq('partner_id', partnerId)
      .or(`confirmed_name.ilike.%${searchQuery}%,whatsapp_name.ilike.%${searchQuery}%,phone_number.ilike.%${searchQuery}%`)
      .limit(5)) as any;

      setSearchResults(
        (data ?? []).map((c) => ({
          id: c.id,
          name: c.confirmed_name || c.whatsapp_name || c.phone_number || '',
          phone: c.phone_number || '',
        }))
      );
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, partnerId]);

  const close = () => {
    setActive(null);
    setSearchQuery('');
    setSearchResults([]);
    setSelectedClient(null);
    setNoteText('');
    setInstructionText('');
    setSaving(false);
    setHoldDays(7);
  };

  const handlePauseClient = async () => {
    if (!selectedClient) return;
    setSaving(true);
    const holdUntil = new Date(Date.now() + holdDays * 24 * 60 * 60 * 1000).toISOString();
    const { error } = await (((supabase.from('semmy_contacts') as any)
      .update({
        hold_until: holdUntil,
        hold_reason: `Gepauzeerd door operator via Semmy chat op ${new Date().toLocaleDateString('nl-NL')}`,
      })
      .eq('id', selectedClient.id))) as any;

    setSaving(false);
    if (!error) {
      onActionComplete(
        `${selectedClient.name} is gepauzeerd tot ${new Date(holdUntil).toLocaleDateString('nl-NL')}. Semmy stuurt geen berichten naar deze klant totdat de pauze verloopt.`
      );
      close();
    }
  };

  const handleAddNote = async () => {
    if (!selectedClient || !noteText.trim()) return;
    setSaving(true);

    const { data: existing } = await (supabase
      .from('semmy_contacts')
      .select('notes')
      .eq('id', selectedClient.id)
      .single()) as any;

    const timestamp = new Date().toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
    const newNote = `[${timestamp}] ${noteText.trim()}`;
    const combined = existing?.notes
      ? `${existing.notes}\n${newNote}`
      : newNote;

    const { error } = await (((supabase.from('semmy_contacts') as any)
      .update({ notes: combined })
      .eq('id', selectedClient.id))) as any;

    setSaving(false);
    if (!error) {
      onActionComplete(`Notitie toegevoegd voor ${selectedClient.name}: "${noteText.trim()}"`);
      close();
    }
  };

  const handleTempInstruction = async () => {
    if (!instructionText.trim()) return;
    setSaving(true);

    const { data: existing } = await (supabase
      .from('semmy_operator_context')
      .select('additional_context')
      .eq('partner_id', partnerId)
      .single()) as any;

    const timestamp = new Date().toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
    const instruction = `[TIJDELIJKE INSTRUCTIE — ${timestamp}]\n${instructionText.trim()}\n[EINDE TIJDELIJKE INSTRUCTIE]`;

    const combined = existing?.additional_context
      ? `${instruction}\n\n${existing.additional_context}`
      : instruction;

    const { error } = await (((supabase.from('semmy_operator_context') as any)
      .update({ additional_context: combined })
      .eq('partner_id', partnerId))) as any;

    setSaving(false);
    if (!error) {
      onActionComplete(`Tijdelijke instructie opgeslagen. Semmy past dit toe bij het volgende gesprek.`);
      close();
    }
  };

  const ClientSearch = () => (
    <div style={{ marginBottom: '12px' }}>
      <input
        autoFocus
        type="text"
        value={searchQuery}
        onChange={e => {
          setSearchQuery(e.target.value);
          setSelectedClient(null);
        }}
        placeholder="Zoek op naam of telefoonnummer..."
        style={{
          width: '100%',
          padding: '8px 12px',
          fontSize: '13px',
          background: 'var(--bg-input)',
          border: '1px solid var(--border-primary)',
          borderRadius: '8px',
          color: 'var(--text-primary)',
          outline: 'none',
        }}
      />
      {searchResults.length > 0 && !selectedClient && (
        <div style={{
          marginTop: '4px',
          border: '1px solid var(--border-primary)',
          borderRadius: '8px',
          overflow: 'hidden',
          background: 'var(--bg-surface-raised)',
        }}>
          {searchResults.map(r => (
            <button
              key={r.id}
              onClick={() => {
                setSelectedClient({ id: r.id, name: r.name });
                setSearchQuery(r.name);
                setSearchResults([]);
              }}
              style={{
                width: '100%',
                padding: '8px 12px',
                fontSize: '12px',
                textAlign: 'left',
                color: 'var(--text-primary)',
                background: 'transparent',
                border: 'none',
                borderBottom: '1px solid var(--border-separator)',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <span>{r.name}</span>
              <span style={{ color: 'var(--text-muted)', fontSize: '11px' }}>{r.phone}</span>
            </button>
          ))}
        </div>
      )}
      {selectedClient && (
        <div style={{
          marginTop: '6px',
          fontSize: '11px',
          color: 'var(--accent)',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
        }}>
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path d="M1.5 5L4 7.5L8.5 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          {selectedClient.name} geselecteerd
        </div>
      )}
    </div>
  );

  const ActionButton = ({ onClick, disabled, children }: { onClick: () => void; disabled?: boolean; children: ReactNode }) => (
    <button
      onClick={onClick}
      disabled={disabled || saving}
      style={{
        padding: '7px 14px',
        fontSize: '12px',
        fontWeight: '500',
        borderRadius: '8px',
        background: disabled || saving ? 'var(--bg-input)' : 'var(--accent)',
        color: disabled || saving ? 'var(--text-muted)' : '#fff',
        border: 'none',
        cursor: disabled || saving ? 'default' : 'pointer',
        transition: 'background 0.15s',
      }}
    >
      {saving ? 'Opslaan...' : children}
    </button>
  );

  const CancelButton = () => (
    <button onClick={close} style={{
      padding: '7px 14px', fontSize: '12px',
      borderRadius: '8px', background: 'transparent',
      color: 'var(--text-muted)', border: '1px solid var(--border-primary)',
      cursor: 'pointer',
    }}>
      Annuleren
    </button>
  );

  if (!partnerId) return null;

  return (
    <div style={{ marginBottom: '8px' }}>
      {!active && (
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {[
            { id: 'pause_client' as ActiveAction, label: 'Pauzeer klant', icon: <Pause size={11} /> },
            { id: 'add_note' as ActiveAction, label: 'Voeg notitie toe', icon: <StickyNote size={11} /> },
            { id: 'temp_instruction' as ActiveAction, label: 'Tijdelijke instructie', icon: <MessageCircle size={11} /> },
          ].map(chip => (
            <button
              key={chip.id}
              onClick={() => setActive(chip.id)}
              style={{
                padding: '5px 12px',
                fontSize: '11px',
                fontWeight: '500',
                borderRadius: '999px',
                background: 'var(--bg-surface-raised)',
                border: '1px solid var(--border-primary)',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                transition: 'border-color 0.15s',
              }}
            >
              {chip.icon}
              {chip.label}
            </button>
          ))}
        </div>
      )}

      {active === 'pause_client' && (
        <div style={{
          padding: '14px',
          background: 'var(--bg-surface-raised)',
          border: '1px solid var(--border-primary)',
          borderRadius: '10px',
        }}>
          <div style={{ fontSize: '12px', fontWeight: '500', marginBottom: '10px', color: 'var(--text-primary)' }}>
            Pauzeer klant — Semmy stuurt geen berichten
          </div>
          <ClientSearch />
          {selectedClient && (
            <div style={{ marginBottom: '10px' }}>
              <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '6px' }}>
                Pauzeer voor:
              </label>
              <div style={{ display: 'flex', gap: '6px' }}>
                {[1, 3, 7, 14].map(days => (
                  <button
                    key={days}
                    onClick={() => setHoldDays(days)}
                    style={{
                      padding: '4px 10px',
                      fontSize: '11px',
                      borderRadius: '6px',
                      border: `1px solid ${holdDays === days ? 'var(--accent)' : 'var(--border-primary)'}`,
                      background: holdDays === days ? 'var(--accent-dim)' : 'transparent',
                      color: holdDays === days ? 'var(--accent)' : 'var(--text-muted)',
                      cursor: 'pointer',
                    }}
                  >
                    {days} dag{days !== 1 ? 'en' : ''}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
            <ActionButton onClick={handlePauseClient} disabled={!selectedClient}>
              Pauzeer {selectedClient?.name ?? 'klant'}
            </ActionButton>
            <CancelButton />
          </div>
        </div>
      )}

      {active === 'add_note' && (
        <div style={{
          padding: '14px',
          background: 'var(--bg-surface-raised)',
          border: '1px solid var(--border-primary)',
          borderRadius: '10px',
        }}>
          <div style={{ fontSize: '12px', fontWeight: '500', marginBottom: '10px', color: 'var(--text-primary)' }}>
            Voeg notitie toe aan klantprofiel
          </div>
          <ClientSearch />
          {selectedClient && (
            <textarea
              autoFocus
              value={noteText}
              onChange={e => setNoteText(e.target.value)}
              placeholder="Bijv. Wil eiken visgraat, budget €3500, deadline voor kerst..."
              rows={3}
              style={{
                width: '100%',
                padding: '8px 12px',
                fontSize: '12px',
                background: 'var(--bg-input)',
                border: '1px solid var(--border-primary)',
                borderRadius: '8px',
                color: 'var(--text-primary)',
                resize: 'none',
                outline: 'none',
                marginBottom: '10px',
                fontFamily: 'inherit',
                lineHeight: '1.5',
              }}
            />
          )}
          <div style={{ display: 'flex', gap: '8px' }}>
            <ActionButton onClick={handleAddNote} disabled={!selectedClient || !noteText.trim()}>
              Notitie opslaan
            </ActionButton>
            <CancelButton />
          </div>
        </div>
      )}

      {active === 'temp_instruction' && (
        <div style={{
          padding: '14px',
          background: 'var(--bg-surface-raised)',
          border: '1px solid var(--border-primary)',
          borderRadius: '10px',
        }}>
          <div style={{ fontSize: '12px', fontWeight: '500', marginBottom: '4px', color: 'var(--text-primary)' }}>
            Tijdelijke instructie voor Semmy
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '10px' }}>
            Semmy past dit toe bij haar volgende gesprekken totdat jij het verwijdert.
          </div>
          <textarea
            autoFocus
            value={instructionText}
            onChange={e => setInstructionText(e.target.value)}
            placeholder="Bijv. Stuur deze week geen offertes — ik ben op vakantie en kan ze niet verwerken..."
            rows={3}
            style={{
              width: '100%',
              padding: '8px 12px',
              fontSize: '12px',
              background: 'var(--bg-input)',
              border: '1px solid var(--border-primary)',
              borderRadius: '8px',
              color: 'var(--text-primary)',
              resize: 'none',
              outline: 'none',
              marginBottom: '10px',
              fontFamily: 'inherit',
              lineHeight: '1.5',
            }}
          />
          <div style={{ display: 'flex', gap: '8px' }}>
            <ActionButton onClick={handleTempInstruction} disabled={!instructionText.trim()}>
              Instructie opslaan
            </ActionButton>
            <CancelButton />
          </div>
        </div>
      )}
    </div>
  );
}


// ── Greeting helper ────────────────────────────────────────────────────────────

function getGreeting(): string {
  const h = new Date().getHours();
  if (h < 6)  return 'Goedenacht.';
  if (h < 12) return 'Goedemorgen.';
  if (h < 18) return 'Goedemiddag.';
  return 'Goedenavond.';
}

// ── Welcome state ─────────────────────────────────────────────────────────────

function WelcomeState({ onSend, isStreaming, onStop, userId, isMobile }: {
  onSend: (message: string) => void;
  isStreaming: boolean;
  onStop: () => void;
  userId: string | null;
  isMobile: boolean;
}) {
  return (
    <div className="h-full flex flex-col items-center justify-center relative">
      <motion.div
        className="relative z-10 w-full max-w-2xl flex flex-col items-center text-center px-5"
        initial={{ opacity: 0, y: 12, filter: 'blur(10px)' }}
        animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      >
        <div className={isMobile ? 'mb-0' : 'mb-10'}>
          <h1
            className="font-bold tracking-tight leading-none mb-3"
            style={{
              fontSize: 'clamp(2.8rem, 4.5vw, 4rem)',
              letterSpacing: '-0.06em',
              color: 'var(--text-primary)',
            }}
          >
            {getGreeting()}
          </h1>
          <p style={{ fontSize: 14, color: 'var(--text-muted)' }}>
            Wat wil je bespreken?
          </p>
        </div>

        {!isMobile && (
          <motion.div
            className="w-full"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.12, duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
          >
            <BarInner
              mode="partner"
              isMobile={false}
              onSend={onSend}
              isStreaming={isStreaming}
              onStop={onStop}
              customPlaceholder="Stel Semmy een vraag…"
              userId={userId}
              conversationId={null}
            />
          </motion.div>
        )}
      </motion.div>

      {isMobile && (
        <motion.div
          className="fixed left-4 right-4 z-20"
          style={{ bottom: 'calc(60px + env(safe-area-inset-bottom, 0px) + 12px)' }}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.12, duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
        >
          <BarInner
            mode="partner"
            isMobile={true}
            onSend={onSend}
            isStreaming={isStreaming}
            onStop={onStop}
            customPlaceholder="Stel Semmy een vraag…"
            userId={userId}
            conversationId={null}
          />
        </motion.div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function PartnerNovaPage() {
  const { user, effectivePartnerId } = useAuth();
  const { semmyAvatarUrl } = useSemmy(effectivePartnerId);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 1024);
  const [messages,    setMessages]    = useState<Message[]>([]);
  const [isStreaming, setIsStreaming]  = useState(false);
  const [error,       setError]       = useState<string | null>(null);
  const [newMsgIndex, setNewMsgIndex] = useState<number | null>(null);

  const messagesEndRef       = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const isAtBottomRef        = useRef(true);
  const abortRef             = useRef<AbortController | null>(null);

  const stopStreaming = useCallback(() => {
    abortRef.current?.abort();
    setIsStreaming(false);
  }, []);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const operatorInitial = (user?.full_name || user?.email || 'O')[0].toUpperCase();

  // ── Load conversation history on mount ──────────────────────────────────────
  const [msgOffset, setMsgOffset] = useState(0);
  const [hasOlderMessages, setHasOlderMessages] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(isSupabaseConfigured);

  useEffect(() => {
    if (!effectivePartnerId || !isSupabaseConfigured) {
      setHistoryLoading(false);
      return;
    }
    (async () => {
      setHistoryLoading(true);
      const { data } = await supabase
        .from('semmy_conversations')
        .select('role, content')
        .eq('partner_id', effectivePartnerId)
        .eq('client_id', 'operator')
        .order('created_at', { ascending: false })
        .limit(21);
      if (data && data.length > 0) {
        const has = data.length === 21;
        const slice = has ? data.slice(0, 20) : data;
        setHasOlderMessages(has);
        setMsgOffset(slice.length);
        setMessages([...slice].reverse().map(d => ({
          role: (d as { role: string; content: string }).role as 'operator' | 'semmy',
          content: (d as { role: string; content: string }).content,
        })));
      }
      setHistoryLoading(false);
    })();
  }, [effectivePartnerId]);

  const loadOlderMessages = useCallback(async () => {
    if (!effectivePartnerId || !isSupabaseConfigured || loadingOlder) return;
    setLoadingOlder(true);
    const { data } = await supabase
      .from('semmy_conversations')
      .select('role, content')
      .eq('partner_id', effectivePartnerId)
      .eq('client_id', 'operator')
      .order('created_at', { ascending: false })
      .range(msgOffset, msgOffset + 20);
    setLoadingOlder(false);
    if (!data || data.length === 0) { setHasOlderMessages(false); return; }
    setHasOlderMessages(data.length === 21);
    const slice = data.length === 21 ? data.slice(0, 20) : data;
    setMsgOffset(prev => prev + slice.length);
    const older = [...slice].reverse().map(d => ({
      role: (d as { role: string; content: string }).role as 'operator' | 'semmy',
      content: (d as { role: string; content: string }).content,
    }));
    setMessages(prev => [...older, ...prev]);
  }, [effectivePartnerId, msgOffset, loadingOlder]);

  // ── Track scroll position ───────────────────────────────────────────────────
  const handleScroll = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    isAtBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  }, []);

  // ── Auto-scroll when messages change ───────────────────────────────────────
  useEffect(() => {
    if (isAtBottomRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // ── Send message ────────────────────────────────────────────────────────────
  const sendMessage = useCallback(async (text: string) => {
    if (!text.trim() || isStreaming) return;
    setError(null);

    const userMsg: Message = { role: 'operator', content: text };
    const pendingMsg: Message = { role: 'semmy', content: '' };

    setMessages(prev => {
      setNewMsgIndex(prev.length);
      return [...prev, userMsg, pendingMsg];
    });
    setIsStreaming(true);
    isAtBottomRef.current = true; // force scroll for new message

    if (!isSupabaseConfigured) {
      // Graceful offline mode
      setTimeout(() => {
        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            role: 'semmy',
            content: 'Supabase is niet geconfigureerd in deze omgeving. Verbind met de echte backend om met Semmy te praten.',
          };
          return updated;
        });
        setIsStreaming(false);
      }, 800);
      return;
    }

    const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
    const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

    abortRef.current = new AbortController();

    try {
      const res = await fetch(`${supabaseUrl}/functions/v1/semmy-chat`, {
        method: 'POST',
        headers: {
          'Content-Type':  'application/json',
          'Authorization': `Bearer ${supabaseKey}`,
        },
        body: JSON.stringify({
          message:              text,
          partner_id:           effectivePartnerId,
          conversation_history: messages.slice(-10),
        }),
        signal: abortRef.current.signal,
      });

      if (!res.ok || !res.body) {
        throw new Error(`HTTP ${res.status}`);
      }

      const reader  = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6).trim();
          if (data === '[DONE]') {
            setIsStreaming(false);
            return;
          }
          try {
            const parsed = JSON.parse(data) as { text?: string; error?: string };
            if (parsed.error) {
              setError(parsed.error);
              setIsStreaming(false);
              return;
            }
            if (parsed.text) {
              setMessages(prev => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last?.role === 'semmy') {
                  updated[updated.length - 1] = { ...last, content: last.content + parsed.text };
                }
                return updated;
              });
            }
          } catch {
            // partial SSE chunk — skip
          }
        }
      }
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'AbortError') {
        setIsStreaming(false);
        return;
      }
      const msg = err instanceof Error ? err.message : 'Verbindingsfout';
      setError(`Kon Semmy niet bereiken: ${msg}`);
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setIsStreaming(false);
      // Replace an empty pending Semmy message with a fallback so it doesn't
      // stay as a stuck thinking indicator.
      setMessages(prev => {
        const last = prev[prev.length - 1];
        if (last?.role === 'semmy' && !last.content) {
          const updated = [...prev];
          updated[updated.length - 1] = { role: 'semmy', content: 'Er ging iets mis — probeer het opnieuw.' };
          return updated;
        }
        return prev;
      });
    }
  }, [isStreaming, messages, effectivePartnerId]);

  const hasMessages = messages.length > 0;

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', overflow: 'hidden' }}>

        {!historyLoading && !hasMessages && !isStreaming ? (
          <div className="h-full">
            <WelcomeState
              onSend={sendMessage}
              isStreaming={isStreaming}
              onStop={stopStreaming}
              userId={effectivePartnerId}
              isMobile={isMobile}
            />
          </div>
        ) : (
          // ── Chat — pill pinned to bottom ───────────────────────────────────
          <>
            <div
              ref={messagesContainerRef}
              onScroll={handleScroll}
              className="flex-1 overflow-y-auto min-h-0"
            >
              <div style={{ maxWidth: '512px', margin: '0 auto', padding: '32px 16px 16px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {hasOlderMessages && (
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <button
                      onClick={loadOlderMessages}
                      disabled={loadingOlder}
                      style={{
                        fontSize: '11px', fontWeight: 500, padding: '5px 14px', borderRadius: '99px',
                        background: 'var(--bg-input)', color: 'var(--text-tertiary)',
                        border: '1px solid var(--border-primary)', cursor: 'pointer', opacity: loadingOlder ? 0.5 : 1,
                      }}
                    >
                      {loadingOlder ? 'Laden…' : 'Laad oudere berichten'}
                    </button>
                  </div>
                )}
                {messages.map((msg, i) => {
                  const isLastSemmy = msg.role === 'semmy' && messages.slice(i + 1).every(m => m.role !== 'semmy');
                  return (
                    <MessageBubble
                      key={i}
                      message={msg}
                      operatorInitial={operatorInitial}
                      isNew={i === newMsgIndex || i === newMsgIndex! + 1}
                      isStreaming={isStreaming && i === messages.length - 1}
                      isLatest={isLastSemmy}
                      semmyImageUrl={semmyAvatarUrl}
                    />
                  );
                })}

                {error && (
                  <div
                    className="rounded-xl px-4 py-3 text-[12px]"
                    style={{ background: 'var(--danger-bg)', color: 'var(--danger-text)', border: '1px solid var(--danger-border)' }}
                  >
                    {error}
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>
            </div>

            {effectivePartnerId && (
              <div className="fade-up stagger-5" style={{ width: '100%', maxWidth: '512px', margin: '0 auto 8px', display: 'flex', justifyContent: 'center' }}>
                <QuickActions
                  partnerId={effectivePartnerId}
                  onActionComplete={(message) => {
                    setMessages(prev => [...prev, { role: 'semmy', content: `✓ ${message}` }]);
                  }}
                />
              </div>
            )}

            {/* Bottom pill — chat mode only */}
            <div
              className="shrink-0 px-4 pt-6 relative"
              style={{ paddingBottom: isMobile ? 'calc(60px + env(safe-area-inset-bottom, 0px) + 16px)' : '16px' }}
            >
              <div style={{ maxWidth: '512px', margin: '0 auto', width: '100%', position: 'relative' }}>
                <BarInner
                  mode="partner"
                  isMobile={false}
                  onSend={sendMessage}
                  isStreaming={isStreaming}
                  onStop={stopStreaming}
                  customPlaceholder="Stel Semmy een vraag…"
                  userId={effectivePartnerId}
                  conversationId={null}
                />
              </div>
            </div>
          </>
        )}

      </div>
    </>
  );
}
